// src/pages/staff/Users.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { RequireStaff } from "@/components/RequireStaff";
import { useUsers } from "@/hooks/useUsers";
import { useLanguage } from "@/contexts/LanguageContext";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserDetailDrawer } from "@/components/staff/UserDetailDrawer";
import {
  Search, Users, Loader2, Shield, Briefcase, Home,
  Gavel, DollarSign, Handshake, UserCog,
  ChevronLeft, ChevronRight, GraduationCap, Star, MapPin, Mail, Phone,
  AlertTriangle,
} from "lucide-react";

const PAGE_SIZE = 50;

const ROLE_TABS = [
  { key: "all", icon: Users, en: "All", fr: "Tous" },
  { key: "admin", icon: Shield, en: "Admin", fr: "Admin" },
  { key: "staff", icon: UserCog, en: "Staff", fr: "Staff" },
  { key: "seller", icon: Home, en: "Sellers", fr: "Vendeurs" },
  { key: "broker", icon: Briefcase, en: "Brokers", fr: "Courtiers" },
  { key: "buyer", icon: Users, en: "Buyers", fr: "Acheteurs" },
  { key: "lender", icon: DollarSign, en: "Lenders", fr: "Prêteurs" },
  { key: "consultant", icon: Gavel, en: "Consultants", fr: "Consultants" },
  { key: "partner", icon: Handshake, en: "Partners", fr: "Partenaires" },
] as const;

// Palette navy/gold AcquizGroup avec couleurs demandées
const ROLE_COLORS: Record<string, string> = {
  admin:      "bg-amber-500/20 text-amber-300 border-amber-500/40",   // or
  staff:      "bg-red-500/20 text-red-300 border-red-500/40",         // rouge
  seller:     "bg-blue-500/20 text-blue-300 border-blue-500/40",      // bleu
  buyer:      "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", // vert
  broker:     "bg-orange-500/20 text-orange-300 border-orange-500/40",   // orange
  lender:     "bg-purple-500/20 text-purple-300 border-purple-500/40",   // violet
  consultant: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
  partner:    "bg-pink-500/20 text-pink-300 border-pink-500/40",
};

const t = {
  en: {
    title: "Users", search: "Search by name, email...",
    id: "Client ID", name: "Name", email: "Email", roles: "Roles",
    joined: "Joined", status: "Status", active: "Active", inactive: "Inactive",
    noUsers: "No users found", prev: "Previous", next: "Next", page: "Page", of: "of",
    tabUsers: "Platform Users", tabExperts: "Our Experts",
    fallbackNotice: "Backend unavailable — showing direct Firestore data",
  },
  fr: {
    title: "Utilisateurs", search: "Rechercher par nom, email...",
    id: "ID Client", name: "Nom", email: "Email", roles: "Rôles",
    joined: "Inscription", status: "Statut", active: "Actif", inactive: "Inactif",
    noUsers: "Aucun utilisateur trouvé", prev: "Précédent", next: "Suivant", page: "Page", of: "de",
    tabUsers: "Utilisateurs plateforme", tabExperts: "Nos Experts",
    fallbackNotice: "Backend indisponible — affichage Firestore direct",
  },
};

// Génère AQ-USR-XXXX depuis les 4 premiers chars du UID
const clientId = (uid?: string) =>
  uid ? `AQ-USR-${uid.slice(0, 4).toUpperCase()}` : "—";

const formatDate = (v: any, lang: string) => {
  if (!v) return "—";
  try {
    const d = v?.toDate ? v.toDate() : v?.seconds ? new Date(v.seconds * 1000) : new Date(v);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA",
      { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
};

const getUserRoles = (u: any): string[] => {
  if (Array.isArray(u?.roles) && u.roles.length) return u.roles;
  if (u?.role) return [u.role];
  return [];
};

const isActive = (u: any): boolean => {
  if (typeof u?.disabled === "boolean") return !u.disabled;
  if (u?.status) return String(u.status).toLowerCase() === "active";
  return true;
};

export default function StaffUsers() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const labels = t[language] || t.en;
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeRole, setActiveRole] = useState<string>(searchParams.get("tab") || "all");
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Fallback Firestore
  const [fsUsers, setFsUsers] = useState<any[]>([]);
  const [fsLoading, setFsLoading] = useState(false);
  const [fsError, setFsError] = useState<Error | null>(null);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedSearch(searchInput); setPage(0); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => { setPage(0); }, [activeRole]);

  const { users: apiUsers, loading: apiLoading, error: apiError, total: apiTotal } = useUsers({
    search: debouncedSearch,
    sortBy: "createdAt",
    sortOrder: "desc",
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    role: activeRole === "all" ? undefined : activeRole,
  });

  // Déclenche le fallback si l'API échoue
  useEffect(() => {
    if (apiError && !useFallback) {
      console.warn("[StaffUsers] NestJS /users failed, falling back to Firestore", apiError);
      setUseFallback(true);
    }
  }, [apiError, useFallback]);

  // Charge Firestore une seule fois quand fallback actif
  useEffect(() => {
    if (!useFallback || fsUsers.length > 0 || fsLoading) return;
    setFsLoading(true);
    getDocs(query(collection(db, "users"), orderBy("createdAt", "desc")))
      .then(snap => {
        setFsUsers(snap.docs.map(d => ({ id: d.id, uid: d.id, ...d.data() })));
        setFsLoading(false);
      })
      .catch(err => {
        console.error("[StaffUsers] Firestore fallback failed", err);
        setFsError(err);
        setFsLoading(false);
      });
  }, [useFallback, fsUsers.length, fsLoading]);

  // Dataset final + filtres client-side quand on est en fallback
  const { displayUsers, total, loading } = useMemo(() => {
    if (useFallback) {
      const q = debouncedSearch.trim().toLowerCase();
      let list = fsUsers;
      if (activeRole !== "all") {
        list = list.filter(u => getUserRoles(u).includes(activeRole));
      }
      if (q) {
        list = list.filter(u => {
          const name = (u.displayName || u.name || "").toLowerCase();
          const email = (u.email || "").toLowerCase();
          return name.includes(q) || email.includes(q);
        });
      }
      const totalCount = list.length;
      const paged = list.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
      return { displayUsers: paged, total: totalCount, loading: fsLoading };
    }
    return { displayUsers: apiUsers, total: apiTotal, loading: apiLoading };
  }, [useFallback, fsUsers, fsLoading, apiUsers, apiTotal, apiLoading, debouncedSearch, activeRole, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const openDrawer = (user: any) => { setSelectedUser(user); setDrawerOpen(true); };

  return (
    <RequireStaff>
      <div className="space-y-4 font-['Geist',sans-serif]">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-white">
            {labels.title} {!loading && <span className="text-gray-400 font-normal">({total})</span>}
          </h1>
        </header>

        {useFallback && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{labels.fallbackNotice}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input placeholder={labels.search} value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {ROLE_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActiveTab = activeRole === tab.key;
            return (
              <button key={tab.key} onClick={() => setActiveRole(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  isActiveTab ? "bg-amber-500 text-white shadow-md shadow-amber-500/20"
                    : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/5"
                }`}>
                <Icon className="w-3.5 h-3.5" />
                {language === "fr" ? tab.fr : tab.en}
              </button>
            );
          })}
        </div>

        <div className="rounded-xl overflow-hidden border border-white/10 bg-white/[0.03]">
          {fsError && (
            <div className="p-4 text-red-300 text-sm">
              {String(fsError.message || fsError)}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-5 h-5 animate-spin text-white/30" />
            </div>
          ) : (
            <ScrollArea className="max-h-[calc(100vh-420px)]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-900/95 backdrop-blur-sm z-10">
                  <tr className="border-b border-white/10 text-gray-400 text-xs uppercase tracking-wider">
                    <th className="text-left p-3">{labels.id}</th>
                    <th className="text-left p-3">{labels.name}</th>
                    <th className="text-left p-3">{labels.email}</th>
                    <th className="text-left p-3">{labels.roles}</th>
                    <th className="text-left p-3">{labels.joined}</th>
                    <th className="text-left p-3">{labels.status}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {displayUsers.map((user: any) => {
                    const roles = getUserRoles(user);
                    const active = isActive(user);
                    return (
                      <tr key={user.id || user.uid}
                        onClick={() => openDrawer(user)}
                        className="hover:bg-white/[0.04] transition-colors cursor-pointer">
                        <td className="p-3">
                          <span className="text-xs font-mono text-amber-400/80">
                            {clientId(user.uid || user.id)}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-white/50">
                                {(user.displayName || user.name || user.email)?.[0]?.toUpperCase() || "?"}
                              </span>
                            </div>
                            <span className="font-medium text-white">
                              {user.displayName || user.name || user.email || "—"}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-gray-400 text-xs">{user.email || "—"}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {roles.length ? roles.map((role: string) => (
                              <Badge key={role} variant="outline"
                                className={`text-[10px] h-5 border ${ROLE_COLORS[role] || "text-gray-400 border-white/10"}`}>
                                {role}
                              </Badge>
                            )) : <span className="text-xs text-gray-600">—</span>}
                          </div>
                        </td>
                        <td className="p-3 text-gray-400 text-xs">
                          {formatDate(user.createdAt, language)}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className={`text-[10px] h-5 border ${
                            active ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                                   : "bg-gray-500/15 text-gray-400 border-gray-500/30"
                          }`}>
                            {active ? labels.active : labels.inactive}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                  {!displayUsers.length && (
                    <tr><td className="p-8 text-center text-gray-500" colSpan={6}>{labels.noUsers}</td></tr>
                  )}
                </tbody>
              </table>
            </ScrollArea>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-white/5 text-gray-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 transition-all">
              <ChevronLeft className="w-4 h-4" /> {labels.prev}
            </button>
            <span className="text-sm text-gray-400">{labels.page} {page + 1} {labels.of} {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-white/5 text-gray-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 transition-all">
              {labels.next} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <UserDetailDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} user={selectedUser} />
    </RequireStaff>
  );
}
